import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartUpdateEvent, CartErrorEvent } from '@theme/events';
import { morphSection } from '@theme/section-renderer';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that manages bulk purchase functionality with automatic discount application.
 *
 * @typedef {object} BulkPurchaseRefs
 * @property {HTMLElement} bulkPrice - The element displaying the bulk price
 * @property {HTMLElement} originalPrice - The element displaying the original price
 * @property {HTMLElement[]} quantityButton - Array of quantity option buttons
 * @property {HTMLElement} addToCartButton - The add to cart button
 * @property {HTMLElement} errorMessage - The error message container
 * @property {HTMLElement} errorText - The error text element
 * @property {HTMLElement} quantityOptions - The quantity options container
 * @extends Component<BulkPurchaseRefs>
 */
class BulkPurchaseComponent extends Component {
  requiredRefs = ['bulkPrice', 'addToCartButton', 'errorMessage', 'errorText', 'quantityOptions'];
  
  /** @type {string | null} */
  #baseOfferText = null;

  /** @type {number | null} */
  #selectedQuantity = null;

  /** @type {string | null} */
  #selectedDiscountCode = null;

  /** @type {number | null} */
  #selectedDiscountPercent = null;

  /** @type {number | null} */
  #basePrice = null;

  /** @type {Array<{quantity: number, discount_code: string, discount_percent: number}>} */
  #pricingTiers = [];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    // Initialize variant availability from data attribute
    const variantAvailable = this.dataset.variantAvailable !== 'false';
    this.dataset.variantAvailable = variantAvailable ? 'true' : 'false';

    // Store base offer text for dynamic percentage updates
    const offerTextElement = this.querySelector('[ref="offerText"]');
    if (offerTextElement && offerTextElement instanceof HTMLElement) {
      this.#baseOfferText = offerTextElement.textContent || offerTextElement.innerText || '';
    }

    // Listen for cart updates to check quantity limits (using theme's default events)
    const { signal } = this.#abortController;
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    // Initial button state check
    this.#checkButtonState();

    // Load pricing data from JSON script tag - try multiple selectors
    let pricingDataScript = this.querySelector('script[ref="bulkPricingData"]');
    if (!pricingDataScript) {
      // Fallback: try finding script by type
      pricingDataScript = this.querySelector('script[type="application/json"]');
    }
    
    if (pricingDataScript) {
      try {
        const parsed = JSON.parse(pricingDataScript.textContent || '[]');
        this.#pricingTiers = Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        console.error('Failed to parse bulk pricing data:', error);
        this.#pricingTiers = [];
      }
    }

    // Get base price from variant (from Liquid data attribute)
    const variantPrice = this.dataset.variantPrice;
    if (variantPrice) {
      this.#basePrice = parseInt(variantPrice, 10);
    }

    // Select first tier by default - try immediately first, then with delays
    // This ensures it works even if timing is off
    this.#selectFirstTier();
    
    // Also try after animation frames to catch any late-loading elements
    requestAnimationFrame(() => {
      if (this.#selectedQuantity === null) {
        this.#selectFirstTier();
      }
      requestAnimationFrame(() => {
        if (this.#selectedQuantity === null) {
          this.#selectFirstTier();
        }
      });
    });

    // Listen for variant changes
    const target = this.closest('.shopify-section, dialog, product-card');
    if (target) {
      target.addEventListener('variantUpdate', this.#onVariantUpdate);
      target.addEventListener('variantSelected', this.#onVariantUpdate);
    }
  }

  updatedCallback() {
    super.updatedCallback();
    // Re-select first tier when component is re-rendered
    if (this.#selectedQuantity === null) {
      queueMicrotask(() => {
        this.#selectFirstTier();
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();

    const target = this.closest('.shopify-section, dialog, product-card');
    if (target) {
      target.removeEventListener('variantUpdate', this.#onVariantUpdate);
      target.removeEventListener('variantSelected', this.#onVariantUpdate);
    }
  }

  /**
   * Checks button state based on inventory and cart quantity (50 max)
   * Uses theme's default event flow
   */
  async #checkButtonState() {
    const variantId = this.dataset.variantId;
    if (!variantId) return;

    const isVariantAvailable = this.dataset.variantAvailable !== 'false';
    
    if (!isVariantAvailable) {
      if (this.refs.addToCartButton instanceof HTMLButtonElement) {
        this.refs.addToCartButton.disabled = true;
      }
      return;
    }

    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      const cartItem = cart.items.find(
        /** @param {any} item */
        (item) => item.variant_id.toString() === variantId.toString()
      );
      const cartQty = cartItem ? cartItem.quantity : 0;
      const MAX_CART_QUANTITY = 50;

      // Disable button if already at 50 limit
      if (cartQty >= MAX_CART_QUANTITY && this.refs.addToCartButton instanceof HTMLButtonElement) {
        this.refs.addToCartButton.disabled = true;
      } else if (this.refs.addToCartButton instanceof HTMLButtonElement) {
        this.refs.addToCartButton.disabled = false;
      }
    } catch (error) {
      console.error('Failed to check cart quantity in bulk purchase:', error);
      // Enable button if check fails (allow user to try)
      if (this.refs.addToCartButton instanceof HTMLButtonElement) {
        this.refs.addToCartButton.disabled = !isVariantAvailable;
      }
    }
  }

  /**
   * Handles cart updates to check and disable button when 50 limit is reached
   * Uses theme's default CartUpdateEvent/CartAddEvent flow
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'bulk-purchase-component') return;

    // Re-check button state after cart update
    await this.#checkButtonState();
  };

  /**
   * Selects the first tier by default
   */
  #selectFirstTier() {
    // Check if we have pricing tiers
    if (!this.#pricingTiers || this.#pricingTiers.length === 0) {
      // Try to reload pricing tiers if not available
      let pricingDataScript = this.querySelector('script[ref="bulkPricingData"]');
      if (!pricingDataScript) {
        pricingDataScript = this.querySelector('script[type="application/json"]');
      }
      if (pricingDataScript) {
        try {
          const parsed = JSON.parse(pricingDataScript.textContent || '[]');
          this.#pricingTiers = Array.isArray(parsed) ? parsed : [];
        } catch (error) {
          console.error('Failed to parse bulk pricing data:', error);
        }
      }
      
      // If still no tiers, retry
      if (!this.#pricingTiers || this.#pricingTiers.length === 0) {
        if (!this.#selectFirstTierAttempts) {
          this.#selectFirstTierAttempts = 0;
        }
        this.#selectFirstTierAttempts++;
        
        if (this.#selectFirstTierAttempts < 30) {
          setTimeout(() => {
            this.#selectFirstTier();
          }, 100);
        }
        return;
      }
    }
    
    let quantityButtons = /** @type {HTMLElement[]} */ (this.refs.quantityButton || []);
    
    // Fallback: query buttons directly if refs aren't available
    if (quantityButtons.length === 0) {
      const buttons = this.querySelectorAll('.bulk-purchase__quantity-btn');
      if (buttons.length > 0) {
        quantityButtons = Array.from(buttons).filter((btn) => btn instanceof HTMLElement);
      }
    }
    
    // If buttons are still not found, try again after a short delay
    if (quantityButtons.length === 0) {
      if (!this.#selectFirstTierAttempts) {
        this.#selectFirstTierAttempts = 0;
      }
      this.#selectFirstTierAttempts++;
      
      if (this.#selectFirstTierAttempts < 30) {
        setTimeout(() => {
          this.#selectFirstTier();
        }, 100);
      }
      return;
    }
    
    // Reset attempt counter on success
    this.#selectFirstTierAttempts = 0;
    
    // Always select the first tier to ensure state is properly initialized
    if (this.#pricingTiers.length > 0 && this.#pricingTiers[0]) {
      const firstTier = this.#pricingTiers[0];
      
      // Get values from first button's data attributes if available, otherwise use tier data
      const firstButton = quantityButtons[0];
      if (firstButton && firstButton instanceof HTMLElement) {
        const quantity = parseInt(firstButton.dataset.quantity || String(firstTier.quantity || 10), 10);
        const discountCode = firstButton.dataset.discountCode || firstTier.discount_code || '';
        const discountPercent = parseFloat(firstButton.dataset.discountPercent || String(firstTier.discount_percent || 0));
        
        this.#selectQuantity(quantity, discountCode, discountPercent);
      } else {
        // Fallback to tier data
        this.#selectQuantity(
          firstTier.quantity || 10,
          firstTier.discount_code || '',
          firstTier.discount_percent || 0
        );
      }
    } else {
      // If no tiers, disable the add to cart button
      if (this.refs.addToCartButton && this.refs.addToCartButton instanceof HTMLButtonElement) {
        this.refs.addToCartButton.disabled = true;
      }
    }
  }
  
  /** @type {number} */
  #selectFirstTierAttempts = 0;

  /**
   * Handles variant updates
   * @param {CustomEvent} event
   */
  #onVariantUpdate = (event) => {
    const detail = /** @type {any} */ (event.detail);
    const variant = detail?.resource;
    const variantId = variant?.id || detail?.data?.variantId;
    
    if (variantId) {
      this.dataset.variantId = variantId.toString();
      
      // Get price and inventory policy from variant resource if available
      if (variant && variant.price) {
        this.#basePrice = variant.price;
        this.dataset.variantPrice = variant.price.toString();
        
        // Update inventory policy if available
        if (variant.inventory_policy) {
          this.dataset.variantInventoryPolicy = variant.inventory_policy;
        }
        
        // Update pricing if quantity is already selected
        if (this.#selectedQuantity) {
          this.#updatePricing();
        }
      } else {
        // Try to get price from the HTML response
        const html = detail?.data?.html;
        if (html) {
          const priceElement = html.querySelector('product-price [ref="priceContainer"]');
          if (priceElement) {
            // Extract price from the price element (this is a fallback)
            // The variant resource should have the price, but if not, we can try this
            const priceText = priceElement.textContent?.match(/[\d,]+\.?\d*/);
            if (priceText) {
              // This is a fallback - ideally we get price from variant resource
              // For now, we'll rely on variant resource having the price
            }
          }
        }
      }
    }
  }

  /**
   * Handles quantity selection
   * @param {MouseEvent} event
   */
  selectQuantity(event) {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (!target) return;
    
    const button = /** @type {HTMLElement | null} */ (target.closest('.bulk-purchase__quantity-btn'));
    if (!button || !(button instanceof HTMLElement)) return;

    const quantity = parseInt(button.dataset.quantity || '0', 10);
    const discountCode = button.dataset.discountCode || '';
    const discountPercent = parseFloat(button.dataset.discountPercent || '0');

    this.#selectQuantity(quantity, discountCode, discountPercent);
  }

  /**
   * Selects a quantity tier and updates UI
   * @param {number} quantity
   * @param {string} discountCode
   * @param {number} discountPercent
   */
  #selectQuantity(quantity, discountCode, discountPercent) {
    this.#selectedQuantity = quantity;
    this.#selectedDiscountCode = discountCode;
    this.#selectedDiscountPercent = discountPercent;

    // Update button states
    const quantityButtons = /** @type {HTMLElement[]} */ (this.refs.quantityButton || []);
    quantityButtons.forEach((btn) => {
      if (!(btn instanceof HTMLElement)) return;
      const btnQuantity = parseInt(btn.dataset.quantity || '0', 10);
      if (btnQuantity === quantity) {
        btn.classList.add('bulk-purchase__quantity-btn--active');
      } else {
        btn.classList.remove('bulk-purchase__quantity-btn--active');
      }
    });

    // Enable add to cart button
    if (this.refs.addToCartButton && this.refs.addToCartButton instanceof HTMLButtonElement) {
      this.refs.addToCartButton.disabled = false;
    }

    // Update offer text with discount percentage
    this.#updateOfferText();

    // Update pricing display
    this.#updatePricing();
  }

  /**
   * Updates the offer text with the selected discount percentage
   */
  #updateOfferText() {
    const offerTextElement = this.querySelector('[ref="offerText"]');
    if (!offerTextElement || !(offerTextElement instanceof HTMLElement)) {
      return;
    }

    // Store base text on first call if not already stored
    if (!this.#baseOfferText) {
      this.#baseOfferText = offerTextElement.textContent?.trim() || offerTextElement.innerText?.trim() || '';
    }

    // If no discount percent, use original text
    if (!this.#selectedDiscountPercent || this.#selectedDiscountPercent === 0) {
      if (this.#baseOfferText) {
        offerTextElement.textContent = this.#baseOfferText;
      }
      return;
    }

    const discountPercent = Math.round(this.#selectedDiscountPercent);
    let baseText = this.#baseOfferText;

    // If base text is empty, use a default format
    if (!baseText || baseText.trim() === '') {
      baseText = 'BUY IN BULK & GET % OFF';
    }

    // Replace percentage in text (handles various formats like "20%", "20 %", etc.)
    // First, try to replace existing percentage
    let updatedText = baseText.replace(/\d+\s*%/g, `${discountPercent}%`);
    
    // If no percentage was found, try to insert it before "OFF"
    if (updatedText === baseText) {
      if (baseText.toUpperCase().includes('OFF')) {
        // Insert percentage before OFF
        updatedText = baseText.replace(/(\s+OFF)/i, ` ${discountPercent}%$1`);
      } else if (baseText.includes('%')) {
        // Has % but no number before it, add number
        updatedText = baseText.replace(/%/g, `${discountPercent}%`);
      } else {
        // No percentage found, append it
        updatedText = `${baseText} ${discountPercent}%`;
      }
    }

    offerTextElement.textContent = updatedText;
  }

  /**
   * Updates the pricing display based on selected quantity
   */
  #updatePricing() {
    // Try to get price from data attribute if not set
    if (!this.#basePrice) {
      const variantPrice = this.dataset.variantPrice;
      if (variantPrice) {
        this.#basePrice = parseInt(variantPrice, 10);
      }
    }
    
    if (!this.#basePrice || !this.#selectedQuantity) {
      return;
    }

    const basePrice = this.#basePrice || 0;
    const selectedQuantity = this.#selectedQuantity || 0;
    const originalTotal = basePrice * selectedQuantity;
    const discountAmount = (originalTotal * (this.#selectedDiscountPercent || 0)) / 100;
    const discountedTotal = originalTotal - discountAmount;

    // Format prices
    const formattedDiscounted = this.#formatMoney(discountedTotal);
    const formattedOriginal = this.#formatMoney(originalTotal);

    // Update price displays
    if (this.refs.bulkPrice && this.refs.bulkPrice instanceof HTMLElement) {
      this.refs.bulkPrice.textContent = formattedDiscounted;
    }

    // originalPrice is optional (only shown if block_settings.show_original_price is true)
    const originalPriceElement = this.querySelector('[ref="originalPrice"]');
    if (originalPriceElement && originalPriceElement instanceof HTMLElement) {
      originalPriceElement.textContent = formattedOriginal;
    }
  }

  /**
   * Formats a price in cents to money format
   * @param {number} cents
   * @returns {string}
   */
  #formatMoney(cents) {
    const shopify = /** @type {any} */ (window.Shopify);
    if (typeof shopify?.formatMoney === 'function') {
      return shopify.formatMoney(cents);
    }
    // Fallback formatting
    return `$${(cents / 100).toFixed(2)}`;
  }

  /**
   * Handles adding bulk items to cart with discount
   * Uses theme's default CartAddEvent/CartUpdateEvent dispatch functions
   * @param {MouseEvent} event
   */
  addBulkToCart = async (event) => {
    event.preventDefault();

    if (!this.#selectedQuantity || !this.#selectedDiscountCode) {
      this.#showError('Please select a quantity option');
      return;
    }

    const variantId = this.dataset.variantId;
    const sectionId = this.dataset.sectionId;

    if (!variantId || !sectionId) {
      this.#showError('Product information is missing');
      return;
    }

    // Check inventory availability
    const isVariantAvailable = this.dataset.variantAvailable !== 'false';
    if (!isVariantAvailable) {
      this.#showError('This product is sold out');
      if (this.refs.addToCartButton && this.refs.addToCartButton instanceof HTMLButtonElement) {
        this.refs.addToCartButton.disabled = true;
      }
      return;
    }

    // Check cart quantity limit (50 max per variant)
    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      const cartItem = cart.items.find(
        /** @param {any} item */
        (item) => item.variant_id.toString() === variantId.toString()
      );
      const cartQty = cartItem ? cartItem.quantity : 0;
      const MAX_CART_QUANTITY = 50;
      const totalAfterAdd = cartQty + this.#selectedQuantity;

      if (totalAfterAdd > MAX_CART_QUANTITY) {
        const availableToAdd = MAX_CART_QUANTITY - cartQty;
        this.#showError(
          `Maximum ${MAX_CART_QUANTITY} items allowed per product. You have ${cartQty} in cart. You can add ${availableToAdd} more.`
        );
        if (this.refs.addToCartButton && this.refs.addToCartButton instanceof HTMLButtonElement) {
          this.refs.addToCartButton.disabled = true;
          setTimeout(() => {
            if (this.refs.addToCartButton && this.refs.addToCartButton instanceof HTMLButtonElement) {
              this.refs.addToCartButton.disabled = false;
            }
          }, 1000);
        }
        return;
      }
    } catch (error) {
      console.error('Failed to check cart quantity:', error);
      // Continue with add to cart if cart check fails
    }

    // Disable button during request
    if (this.refs.addToCartButton && this.refs.addToCartButton instanceof HTMLButtonElement) {
      this.refs.addToCartButton.disabled = true;
      this.refs.addToCartButton.textContent = 'Adding...';
    }

    this.#hideError();

    try {
      // Get cart section IDs for sections parameter (matching default add to cart)
      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      /** @type {string[]} */
      const cartItemComponentsSectionIds = [];
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          cartItemComponentsSectionIds.push(item.dataset.sectionId);
        }
      });

      // Step 1: Add items to cart using FormData (matching default add to cart exactly)
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', this.#selectedQuantity.toString());
      
      // Add line item property for preorder items (matching add-to-cart-button.liquid)
      const inventoryPolicy = this.dataset.variantInventoryPolicy;
      if (inventoryPolicy === 'continue') {
        formData.append('properties[Order Type]', 'Preorder');
      }
      
      // Append sections AFTER the forEach loop (matching default behavior)
      if (cartItemComponentsSectionIds.length > 0) {
        formData.append('sections', cartItemComponentsSectionIds.join(','));
      }

      const fetchCfg = fetchConfig('javascript', { body: formData });

      const addToCartResponse = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'text/html',
        },
      });

      const addToCartData = await addToCartResponse.json();

      // Check for errors in response (matching default add to cart)
      if (addToCartData.status) {
        // Dispatch error event on document
        document.dispatchEvent(
          new CartErrorEvent(
            this.id,
            addToCartData.message,
            addToCartData.description,
            addToCartData.errors
          )
        );

        this.#showError(addToCartData.message || 'Failed to add items to cart');

        // Dispatch CartAddEvent with error flag (for max quantity exceeded)
        document.dispatchEvent(
          new CartAddEvent({}, variantId.toString(), {
            didError: true,
            source: 'bulk-purchase-component',
            itemCount: this.#selectedQuantity,
            productId: this.dataset.productId,
            variantId: variantId.toString(),
          })
        );
        
        if (this.refs.addToCartButton && this.refs.addToCartButton instanceof HTMLButtonElement) {
          this.refs.addToCartButton.disabled = false;
          this.refs.addToCartButton.textContent = this.refs.addToCartButton.dataset.originalText || 'ADD TO CART';
        }

        return;
      }

      // Success path - hide any errors
      this.#hideError();

      // Step 2: Apply discount code
      const updateResponse = await fetch(`${Theme.routes.cart_update_url}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          discount: this.#selectedDiscountCode,
          sections: cartItemComponentsSectionIds.join(','),
        }),
      });

      if (!updateResponse.ok) {
        const errorData = await updateResponse.json();
        throw new Error(errorData.description || 'Failed to apply discount code');
      }

      const updateData = await updateResponse.json();

      // Merge sections from both responses
      const mergedSections = {
        ...(addToCartData.sections || {}),
        ...(updateData.sections || {}),
      };

      // Dispatch CartAddEvent on document (triggers cart drawer and cart icon)
      // Use 'bulk-purchase-component' as source so product form can detect and update
      document.dispatchEvent(
        new CartAddEvent(
          {},
          variantId.toString(),
          {
            source: 'bulk-purchase-component',
            itemCount: this.#selectedQuantity,
            productId: this.dataset.productId,
            variantId: variantId.toString(),
            sections: mergedSections,
          }
        )
      );

      // Dispatch CartUpdateEvent for cart items component updates
      document.dispatchEvent(
        new CartUpdateEvent(updateData, this.id, {
          source: 'bulk-purchase-component',
          variantId: variantId.toString(),
          sections: mergedSections,
        })
      );

      // Update cart sections if needed (matching default behavior)
      if (mergedSections) {
        Object.entries(mergedSections).forEach(([sectionId, html]) => {
          if (html) {
            try {
              morphSection(sectionId, html);
            } catch (error) {
              console.error(`Failed to update section ${sectionId}:`, error);
            }
          }
        });
      }

      // Reset button
      if (this.refs.addToCartButton && this.refs.addToCartButton instanceof HTMLButtonElement) {
        this.refs.addToCartButton.disabled = false;
        this.refs.addToCartButton.textContent = this.refs.addToCartButton.dataset.originalText || 'ADD TO CART';
      }
    } catch (error) {
      console.error('Bulk purchase error:', error);
      this.#showError(error.message || 'Failed to add items to cart. Please try again.');
      
      // Dispatch error event on document
      document.dispatchEvent(
        new CartErrorEvent(
          this.id,
          error.message || 'Failed to add items to cart',
          error.message,
          {}
        )
      );
      
      if (this.refs.addToCartButton && this.refs.addToCartButton instanceof HTMLButtonElement) {
        this.refs.addToCartButton.disabled = false;
        this.refs.addToCartButton.textContent = this.refs.addToCartButton.dataset.originalText || 'ADD TO CART';
      }
    } finally {
      cartPerformance.measureFromEvent('bulk-add:user-action', event);
    }
  };

  /**
   * Shows an error message
   * @param {string} message
   */
  #showError(message) {
    if (this.refs.errorMessage && this.refs.errorText) {
      this.refs.errorText.textContent = message;
      this.refs.errorMessage.classList.remove('hidden');
    }
  }

  /**
   * Hides the error message
   */
  #hideError() {
    if (this.refs.errorMessage) {
      this.refs.errorMessage.classList.add('hidden');
    }
  }
}

if (!customElements.get('bulk-purchase-component')) {
  customElements.define('bulk-purchase-component', BulkPurchaseComponent);
}

